export interface InputState {
  keys: Set<string>;
  mouse: {
    x: number;
    y: number;
    leftButton: boolean;
    rightButton: boolean;
    scrollDelta: number;
  };
  touch: {
    active: boolean;
    x: number;
    y: number;
  };
}

export class InputManager {
  private canvas: HTMLCanvasElement;
  private currentState: InputState;
  private previousState: InputState;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;

    this.currentState = this.createEmptyState();
    this.previousState = this.createEmptyState();

    this.setupEventListeners();
  }

  private createEmptyState(): InputState {
    return {
      keys: new Set(),
      mouse: { x: 0, y: 0, leftButton: false, rightButton: false, scrollDelta: 0 },
      touch: { active: false, x: 0, y: 0 },
    };
  }

  private setupEventListeners(): void {
    // Keyboard events
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);

    // Mouse events
    this.canvas.addEventListener('mousedown', this.onMouseDown);
    this.canvas.addEventListener('mouseup', this.onMouseUp);
    this.canvas.addEventListener('mousemove', this.onMouseMove);

    // Touch events
    this.canvas.addEventListener('touchstart', this.onTouchStart, { passive: false });
    this.canvas.addEventListener('touchend', this.onTouchEnd);
    this.canvas.addEventListener('touchmove', this.onTouchMove, { passive: false });

    // Scroll events
    this.canvas.addEventListener('wheel', this.onWheel, { passive: false });
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    this.currentState.keys.add(e.code);
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    this.currentState.keys.delete(e.code);
  };

  private onMouseDown = (e: MouseEvent): void => {
    if (e.button === 0) this.currentState.mouse.leftButton = true;
    if (e.button === 2) this.currentState.mouse.rightButton = true;
  };

  private onMouseUp = (e: MouseEvent): void => {
    if (e.button === 0) this.currentState.mouse.leftButton = false;
    if (e.button === 2) this.currentState.mouse.rightButton = false;
  };

  private onMouseMove = (e: MouseEvent): void => {
    const rect = this.canvas.getBoundingClientRect();
    this.currentState.mouse.x = e.clientX - rect.left;
    this.currentState.mouse.y = e.clientY - rect.top;
  };

  private onTouchStart = (e: TouchEvent): void => {
    e.preventDefault();
    if (e.touches.length > 0) {
      const touch = e.touches[0];
      const rect = this.canvas.getBoundingClientRect();
      this.currentState.touch.active = true;
      this.currentState.touch.x = touch.clientX - rect.left;
      this.currentState.touch.y = touch.clientY - rect.top;
    }
  };

  private onTouchEnd = (): void => {
    this.currentState.touch.active = false;
  };

  private onTouchMove = (e: TouchEvent): void => {
    e.preventDefault();
    if (e.touches.length > 0) {
      const touch = e.touches[0];
      const rect = this.canvas.getBoundingClientRect();
      this.currentState.touch.x = touch.clientX - rect.left;
      this.currentState.touch.y = touch.clientY - rect.top;
    }
  };

  private onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    this.currentState.mouse.scrollDelta = e.deltaY;
  };

  update(): void {
    // Copy current state to previous state
    this.previousState.keys = new Set(this.currentState.keys);
    this.previousState.mouse = { ...this.currentState.mouse };
    this.previousState.touch = { ...this.currentState.touch };

    // Reset scroll delta after reading
    this.currentState.mouse.scrollDelta = 0;
  }

  // Key state checks
  isKeyDown(code: string): boolean {
    return this.currentState.keys.has(code);
  }

  isKeyPressed(code: string): boolean {
    return this.currentState.keys.has(code) && !this.previousState.keys.has(code);
  }

  isKeyReleased(code: string): boolean {
    return !this.currentState.keys.has(code) && this.previousState.keys.has(code);
  }

  // Mouse state checks
  isMouseButtonDown(button: 'left' | 'right'): boolean {
    return button === 'left'
      ? this.currentState.mouse.leftButton
      : this.currentState.mouse.rightButton;
  }

  isMouseButtonPressed(button: 'left' | 'right'): boolean {
    const current = button === 'left'
      ? this.currentState.mouse.leftButton
      : this.currentState.mouse.rightButton;
    const previous = button === 'left'
      ? this.previousState.mouse.leftButton
      : this.previousState.mouse.rightButton;
    return current && !previous;
  }

  getMousePosition(): { x: number; y: number } {
    return { x: this.currentState.mouse.x, y: this.currentState.mouse.y };
  }

  isMouseDown(): boolean {
    return this.currentState.mouse.leftButton;
  }

  wasMouseClicked(): boolean {
    return !this.currentState.mouse.leftButton && this.previousState.mouse.leftButton;
  }

  getScrollDelta(): number {
    return this.currentState.mouse.scrollDelta;
  }

  // Touch state checks
  isTouchActive(): boolean {
    return this.currentState.touch.active;
  }

  getTouchPosition(): { x: number; y: number } {
    return { x: this.currentState.touch.x, y: this.currentState.touch.y };
  }

  // Movement helpers (WASD or Arrow keys)
  getMovementVector(): { x: number; y: number } {
    let x = 0;
    let y = 0;

    if (this.isKeyDown('KeyW') || this.isKeyDown('ArrowUp')) y -= 1;
    if (this.isKeyDown('KeyS') || this.isKeyDown('ArrowDown')) y += 1;
    if (this.isKeyDown('KeyA') || this.isKeyDown('ArrowLeft')) x -= 1;
    if (this.isKeyDown('KeyD') || this.isKeyDown('ArrowRight')) x += 1;

    // Normalize diagonal movement
    if (x !== 0 && y !== 0) {
      const length = Math.sqrt(x * x + y * y);
      x /= length;
      y /= length;
    }

    return { x, y };
  }

  // Cleanup
  destroy(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    this.canvas.removeEventListener('mousedown', this.onMouseDown);
    this.canvas.removeEventListener('mouseup', this.onMouseUp);
    this.canvas.removeEventListener('mousemove', this.onMouseMove);
    this.canvas.removeEventListener('touchstart', this.onTouchStart);
    this.canvas.removeEventListener('touchend', this.onTouchEnd);
    this.canvas.removeEventListener('touchmove', this.onTouchMove);
    this.canvas.removeEventListener('wheel', this.onWheel);
  }
}
